<template>
  <v-card
    flat
    class="table-container elevation-2 beam-table-container border-0"
    :class="{
      'mb-0 rounded-b-0 rounded-t-lg': hasRelationalDiagram,
      'mb-4 rounded-lg': !hasRelationalDiagram}">
    <!-- <pre>{{ dataModel }}</pre> -->
    <v-toolbar flat class="data-model-toolbar" height="56">
      <v-col cols="8" style="display: flex;" class="d-flex align-center subheading pb-2">
        <quick-rename-input
          :id="`data-model-name-${indexDataModel}`"
          :value="dataModel.name"
          :disabled="$store.state.isWorkflowDisabled"
          @onChange="blurDataModelName($event)"
          :max-length="30"
        />
      </v-col>
      <v-spacer></v-spacer>
      <v-col v-if="!$store.state.isWorkflowDisabled" xs4 class="text-right pb-2">
        <help-dialog-workflow-entities />
        <v-menu>
          <template #activator="{ props }">
            <v-btn icon="mdi-menu" variant="text" v-bind="props" tabindex="-1" data-test="data-model-actions-menu" />
          </template>
          <v-list>
            <v-list-item @click="focusOnField(`data-model-name-${indexDataModel}`)" v-if="dataModel.type === 'Dimension'">
              <v-list-item-action>
                <v-icon class="mr-2" color="grey">mdi-pencil</v-icon> Edit Name
              </v-list-item-action>
            </v-list-item>
            <v-list-item v-if="!hideDeleteAction" @click="$emit('delete-data-model', indexDataModel)">
              <v-list-item-action>
                <v-icon class="mr-2" color="grey">mdi-delete</v-icon> Delete
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-toolbar>
    <v-table class="rounded-0 elevation-1">
      <template v-slot:default>
        <tbody v-if="dataModel.dataFields.length > 0">
          <template v-for="(row, index) in rows">
            <!-- type: verb -->
            <tr v-if="row.type === 'verb'" :key="index">
              <td v-if="dataModel.dataFields.length > 0" class="field-label" rowspan="2">{{ row['rowLabel'] }}</td>
              <td v-else>Click the plus sign to add a field</td>
              <td v-for="(field, indexDataField) in dataModel.dataFields" :key="indexDataField" class="px-0 field-name-wrapper header-cell header-cell-verb">
                <beam-table-cell
                  :key="idTable(indexDataModel, index, indexDataField)"
                  :ref="idTable(indexDataModel, index, indexDataField)"
                  :tab="idTable(indexDataModel, index, indexDataField)"
                  v-model:value="field[row['fieldName']]"
                  @leave-cell="leaveCell($event, indexDataModel, index, indexDataField)"
                  @blur-table-cell="$emit('blur-table-cell', $event, indexDataModel, indexDataField, row['fieldName'], null, event.id)"
                  tag="div"
                  verb
                />
                <div class="header-action" v-if="!$store.state.isWorkflowDisabled">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small" color="#1F2833" class="captures-description" tabindex="0" @click="$emit('move-left', indexDataModel, indexDataField)">mdi-chevron-left</v-icon>
                    </template>
                    Move column left
                  </v-tooltip>
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small" color="#1F2833" class="captures-description" tabindex="0" @click="$emit('move-right', indexDataModel, indexDataField)">mdi-chevron-right</v-icon>
                    </template>
                    Move column right
                  </v-tooltip>
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small" color="#1F2833" @click="$emit('delete-data-field', indexDataModel, indexDataField)">mds:delete</v-icon>
                    </template>
                    Delete column
                  </v-tooltip>
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small" color="#1F2833" class="captures-description" tabindex="0" @click="$emit('add-column', indexDataModel, indexDataField)">mdi-plus</v-icon>
                    </template>
                    Add column after
                  </v-tooltip>
                </div>
              </td>
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <td
                    v-bind="props"
                    class="add-field-button px-0 header-cell text-center captures-description"
                    tabindex="0"
                    @click="!$store.state.isWorkflowDisabled ? $emit('add-column', indexDataModel) : null"
                    rowspan="2">
                    <v-icon>mds:add</v-icon>
                  </td>
                </template>
                <span>Add column</span>
              </v-tooltip>
            </tr>

            <!-- type: header-after-verb -->
            <tr v-if="row.type === 'header-after-verb'" :key="index">
              <td v-for="(field, indexDataField) in dataModel.dataFields" :key="indexDataField" class="px-0 header-cell">
                <beam-table-cell
                  :key="idTable(indexDataModel, index, indexDataField)"
                  :ref="idTable(indexDataModel, index, indexDataField)"
                  :tab="idTable(indexDataModel, index, indexDataField)"
                  v-model:value="field[row['fieldName']]"
                  @leave-cell="leaveCell($event, indexDataModel, index, indexDataField)"
                  @blur-table-cell="$emit('blur-table-cell', $event, indexDataModel, indexDataField, row['fieldName'], null, event.id)"
                  tag="div"
                  headerAfterVerb
                />
              </td>
            </tr>

            <!-- type: header -->
            <tr v-if="row.type === 'header'" :key="index" ref="sortableList">
              <td class="field-label">{{ dataModel.dataFields.length > 0 ? row['rowLabel'] + ':' : 'Click the plus sign to add a field' }}</td>
              <td
                disabled="true"
                v-for="(field, indexDataField) in dataModel.dataFields"
                :key="refreshKey * dataModel.dataFields.length + indexDataField"
                class="header-cell px-0 field-name-wrapper sortableRow"
              >
                <div class="d-flex align-center h-100">
                  <div
                    v-if="field.primaryKey || field.reference"
                    class="primary-key-icon d-inline-flex align-center h-100 pl-2"
                  >
                    <v-tooltip v-if="field.primaryKey" location="bottom" open-delay="500">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="small" icon="mdi-key" color="#1F2833" />
                      </template>
                      <span>Primary key</span>
                    </v-tooltip>
                    <v-tooltip v-else location="bottom" open-delay="500">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="small" icon="mdi-link" color="#1F2833" />
                      </template>
                      <span>Reference to entity</span>
                    </v-tooltip>
                  </div>
                  <beam-table-cell
                    :key="idTable(indexDataModel, index, indexDataField)"
                    :ref="idTable(indexDataModel, index, indexDataField)"
                    :tab="idTable(indexDataModel, index, indexDataField)"
                    :is-read-model="dataModel.type === 'Read Model'"
                    :class="{'pl-1': field.primaryKey}"
                    v-model:value="field[row['fieldName']]"
                    @leave-cell="leaveCell($event, indexDataModel, index, indexDataField)"
                    @blur-table-cell="$emit('blur-table-cell', $event, indexDataModel, indexDataField, row['fieldName'], null, event?.id)"
                    tag="div"
                    header
                  />
                </div>
                <v-icon class="sortHandle" v-if='!$store.state.isWorkflowDisabled'>mdi-drag-horizontal</v-icon>
                <action-menu
                  v-if="!$store.state.isWorkflowDisabled"
                  class="header-action"
                  :menu-items="[
                    {
                      id: 1,
                      icon: field.primaryKey ? 'mdi-key-remove' : 'mdi-key',
                      text: field.primaryKey ? 'Unset primary key' : 'Set as primary key',
                      action: () => setPrimaryKey(indexDataModel, indexDataField)
                    },
                    {
                      id: 4,
                      icon: field.reference ? 'mdi-link-off' : 'mdi-link',
                      text: field.reference ? 'Unset reference' : 'Set as reference',
                      action: () => setReference(indexDataModel, indexDataField)
                    },
                    {
                      id: 5,
                      icon: 'mdi-asterisk',
                      text: field.tags && field.tags.includes('REQUIRED') ? 'Set as optional' : 'Set as required',
                      action: () => setFieldMandatory(indexDataModel, indexDataField, !(field.tags && field.tags.includes('REQUIRED')))
                    },
                    {
                      id: 3,
                      icon: 'mds:temp_preferences_custom',
                      text: field.exampleData?.some((i) => !!i) ? 'Regenerate example data' : 'Generate example data',
                      action: () => generateExampleData(indexDataModel, indexDataField)
                    },
                    {
                      id: 2,
                      icon: 'mdi-delete',
                      text: 'Delete',
                      action: () => $emit('delete-data-field', indexDataModel, indexDataField)
                    }
                  ]"
                />
                <div class="header-action" v-else>
                  <v-tooltip v-if="isMissingEntity(field[row['fieldName']])" location="right">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" style="cursor: default;">⚠️</span>
                    </template>
                    <span>This field on the command has no corresponding <br /> entity field. Ok for transient arguments</span>
                  </v-tooltip>
                  <v-icon
                    v-if="!$store.state.isWorkflowDisabled"
                    size="small"
                    @click="$emit('delete-data-field', indexDataModel, indexDataField)"
                  >
                    mdi-delete
                  </v-icon>
                </div>
              </td>
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <td
                    v-bind="props"
                    class="add-field-button px-0 header-cell text-center captures-description"
                    :class="{ 'bg-green-lighten-4': dataModel.type === 'Read Model' }"
                    tabindex="0"
                    @click="!$store.state.isWorkflowDisabled ? $emit('add-column', indexDataModel) : null">
                    <v-icon>mds:add</v-icon>
                  </td>
                </template>
                <span>Add column</span>
              </v-tooltip>
            </tr>

            <!-- type: text -->
            <tr v-if="row.type === 'text'" :key="index">
              <td class="field-label">{{ row['rowLabel'] }}:</td>
              <beam-table-cell
                v-for="(field, indexDataField) in dataModel.dataFields" :key="idTable(indexDataModel, index, indexDataField)"
                :ref="idTable(indexDataModel, index, indexDataField)"
                :tab="idTable(indexDataModel, index, indexDataField)"
                v-model:value="field[row['fieldName']]"
                @leave-cell="leaveCell($event, indexDataModel, index, indexDataField)"
                @blur-table-cell="$emit('blur-table-cell', $event, indexDataModel, indexDataField, row['fieldName'], null, event.id)"
                cell
              />
              <td></td>
            </tr>

            <!-- type: relationship-type-select -->
            <tr v-if="row.type === 'relationship-type-select'" class='relationship-type' :key="index">
              <td class="field-label">{{ row['rowLabel'] }}:</td>
              <template v-for="(field, indexDataField) in dataModel.dataFields" :key="idTable(indexDataModel, index, indexDataField)">
                <beam-table-cell-select
                  :ref="idTable(indexDataModel, index, indexDataField)"
                  :tab="idTable(indexDataModel, index, indexDataField)"
                  v-model:value="field[row['fieldName']]"
                  :types="relationshipTypes"
                  @leave-cell="leaveCell($event, indexDataModel, index, indexDataField)"
                  @blur="$emit('blur-table-cell', $event, indexDataModel, indexDataField, row['fieldName'], null, event.id)"
                  @click-type="$emit('click-type', $event, indexDataModel, indexDataField)" />
              </template>
              <td class="beam-table-cell-select"></td>
            </tr>

            <!-- type: select -->
            <tr v-if="row.type === 'select'" :key="index">
              <td class="field-label">{{ row['rowLabel'] }}:</td>
              <template v-for="(field, indexDataField) in dataModel.dataFields" :key="idTable(indexDataModel, index, indexDataField)">
                <beam-table-cell-select
                  :ref="idTable(indexDataModel, index, indexDataField)"
                  :tab="idTable(indexDataModel, index, indexDataField)"

                  v-model:value="field[row['fieldName']]"
                  :types="types"
                  @leave-cell="leaveCell($event, indexDataModel, index, indexDataField)"
                  @blur="$emit('blur-table-cell', $event, indexDataModel, indexDataField, row['fieldName'], null, event.id)"
                  @click-type="$emit('click-type', $event, indexDataModel, indexDataField)" />
              </template>
              <td class="beam-table-cell-select"></td>
            </tr>

            <!-- type: dimension -->
            <tr v-if="row.type === 'dimension'" :key="index">
              <td class="field-label">Dimension / Fact:</td>
              <template v-for="(field, indexDataField) in dataModel.dataFields" :key="idTable(indexDataModel, index, indexDataField)">
                <beam-table-cell-select-from-table
                  :ref="idTable(indexDataModel, index, indexDataField)"
                  :tab="idTable(indexDataModel, index, indexDataField)"
                  v-model:value="field[row['fieldName']]"
                  :headers="dimensionsHeaders"
                  :items="dimensions"
                  @leave-cell="leaveCell($event, indexDataModel, index, indexDataField)"
                  @blur="$emit('blur-table-cell', $event, indexDataModel, indexDataField, row['fieldName'], null, event.id)"
                  @click-type="$emit('click-type', $event, indexDataModel, indexDataField)"
                />
              </template>
              <td></td>
            </tr>

            <!-- type: dimension-data -->
            <tr v-if="row.type === 'dimension-data'" :key="index">
              <td class="field-label">{{ row['rowLabel'] }}</td>
              <template v-for="(field, indexDataField) in dataModel.dataFields" :key="idTable(indexDataModel, index, indexDataField)">
                <beam-table-cell
                  :ref="idTable(indexDataModel, index, indexDataField)"
                  :tab="idTable(indexDataModel, index, indexDataField)"
                  v-model:value="field[row['fieldName']]"
                  @leave-cell="leaveCell($event, indexDataModel, index, indexDataField)"
                  @blur-table-cell="$emit('blur-table-cell', $event, indexDataModel, indexDataField, row['fieldName'], null, event.id)"
                  cell
                />
              </template>
              <td></td>
            </tr>

            <!-- type: checkbox -->
            <tr v-if="row.type === 'checkbox'" :key="index">
              <td class="field-label">
                {{ row['rowLabel'] }}
                <v-tooltip v-if="row.tooltip" >
                  <template v-slot:activator="{ props }">
                    <v-icon size="x-small" v-bind="props" class="ml-0">mdi-help-circle-outline</v-icon>
                  </template>
                  <span>{{ row.tooltip }}</span>
                </v-tooltip>
              </td>
              <template v-for="(field, indexDataField) in dataModel.dataFields" :key="idTable(indexDataModel, index, indexDataField)">
                <beam-table-cell-checkbox
                  :ref="idTable(indexDataModel, index, indexDataField)"
                  :tab="idTable(indexDataModel, index, indexDataField)"
                  :tags="field['tags']"
                  @leave-cell="leaveCell($event, indexDataModel, index, indexDataField)"
                  @set-tags="$emit('click-tags', $event, indexDataModel, indexDataField)"
                />
              </template>
              <td></td>
            </tr>

            <!-- type: relation -->
            <tr v-if="row.type === 'relation'" :key="index">
              <td class="field-label">
                {{ row.rowLabel || 'Related entities' }}:
              </td>
              <template v-for="(field, indexDataField) in dataModel.dataFields" :key="idTable(indexDataModel, index, indexDataField)">
                <data-model-select-entity
                  :ref="idTable(indexDataModel, index, indexDataField)"
                  :id="idTable(indexDataModel, index, indexDataField)"
                  :eventId="event?.id"
                  :entityId="field[row.fieldName]"
                  :dataField="field"
                  :dataFieldIndex="indexDataField"
                  :dataModelIndex="indexDataModel"
                  :isEntity="dataModel.type === 'Dimension'"
                  @leave-cell="leaveCell($event, indexDataModel, index, indexDataField)"
                />
              </template>
              <td></td>
            </tr>

            <!-- type: example-data -->
            <template v-if="row.type === 'example-data'">
              <tr v-for="exampleIndex in (getNumberOfExampleDataRows(dataModel.dataFields) + 3)" :key="index + (exampleIndex - 1)">
                <td class="field-label" v-if="exampleIndex === 1">
                  Example 1:
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon size="x-small" v-bind="props" class="ml-0">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Ask for the most common example of data for each field.</span>
                  </v-tooltip>
                </td>
                <td class="field-label" v-if="exampleIndex === 2">
                  Example 2:
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon size="x-small" v-bind="props" class="ml-0">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Ask for the second most common example that is different from the typical row above.</span>
                  </v-tooltip>
                </td>
                <td class="field-label" v-if="exampleIndex === 3">
                  Example 3:
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon size="x-small" v-bind="props" class="ml-0">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Ask for the third most common example that is different from both rows above.</span>
                  </v-tooltip>
                </td>
                <td class="field-label" v-if="exampleIndex === 4">
                </td>
                <td class="field-label" v-if="exampleIndex === 5">
                </td>
                <td class="field-label" v-if="exampleIndex === 6">
                </td>
                <td class="field-label" v-if="exampleIndex > 6"></td>
                <template v-for="(field, indexDataField) in dataModel.dataFields" :key="idTable(indexDataModel, index + exampleIndex - 1, indexDataField)">
                  <beam-table-cell
                    :ref="idTable(indexDataModel, index + exampleIndex - 1, indexDataField)"
                    :tab="idTable(indexDataModel, index + exampleIndex - 1, indexDataField)"
                    v-model:value="field.exampleData[exampleIndex - 1]"
                    @leave-cell="leaveCell($event, indexDataModel, index + exampleIndex - 1, indexDataField)"
                    @blur-table-cell="$emit('blur-table-cell', $event, indexDataModel, indexDataField, 'exampleData', exampleIndex - 1, event?.id)"
                    :list="!!field.relatedEntityId && !!field.exampleData[exampleIndex - 1] && isOneToMany(field)"
                    cell
                  />
                </template>
                <td></td>
              </tr>
            </template>
          </template>
        </tbody>
        <tbody v-else>
          <tr>
            <td class="btc-cell">Click the plus sign on the right to add a field!</td>
            <v-tooltip location="bottom">
              <template v-slot:activator="{ props }">
                <td
                  v-bind="props"
                  class="add-field-button px-0 header-cell text-center"
                  @click="!$store.state.isWorkflowDisabled ? $emit('add-column', indexDataModel) : null">
                  <v-icon>mds:add</v-icon>
                </td>
              </template>
              <span>Add a column</span>
            </v-tooltip>
          </tr>
        </tbody>
      </template>
    </v-table>
  </v-card>
</template>

<script>
import ActionMenu from '@/components/ActionMenu.vue'
import BeamTableCell from '@/components/BeamTableCell'
import QuickRenameInput from '@/components/QuickRenameInput.vue'
import BeamTableCellSelect from '@/components/BeamTableCellSelect'
import BeamTableCellCheckbox from '@/components/BeamTableCellCheckbox'
import DataModelSelectEntity from '@/components/DataModelSelectEntity.vue'
import HelpDialogWorkflowEntities from '@/components/HelpDialogWorkflowEntities'
import BeamTableCellSelectFromTable from '@/components/BeamTableCellSelectFromTable'
import { diagramHelpers } from '@/mixins/diagramHelpers.js'
import workflowApi from '@/api/workflowApi'
import requirementTypesApi from '@/api/requirementTypesApi'
import { mapState } from 'vuex'
import Sortable from 'sortablejs'
import { EventBus } from '@/event-bus/event-bus.js'
import useEntityActions from '@/composables/useEntityActions'

export default {
  name: 'entity-table',

  mixins: [diagramHelpers],

  components: {
    ActionMenu,
    DataModelSelectEntity,
    BeamTableCell,
    QuickRenameInput,
    BeamTableCellSelect,
    BeamTableCellSelectFromTable,
    BeamTableCellCheckbox,
    HelpDialogWorkflowEntities
  },

  data () {
    return {
      types: [
        '[Who]',
        '[What]',
        '[When]',
        '[Where]',
        '[How]',
        '[How many]'
      ],
      relationshipTypes: [
        'one-to-one',
        'one-to-many'
      ],
      dimensionsHeaders: [
        { title: 'Entity', value: 'entity' },
        { title: 'Key', value: 'keyType' },
        { title: 'Source System', value: 'sourceSystem' },
        { title: 'Table name', value: 'table' },
        { title: 'Type', value: 'type' }
      ],
      dataModelTitleMapper: {
        'Event Story': 'Command',
        Dimension: 'Entity'
      },
      refreshKey: 0
    }
  },

  props: {
    dataModel: Object,
    dimensions: Array,
    indexDataModel: Number,
    rows: Array,
    event: Object,
    hasRelationalDiagram: Boolean,
    hideDeleteAction: Boolean,
    cardIndex: Number
  },

  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow,
      requirementTypes: state => state.requirementTypes.requirementTypes
    }),
    entityCard () {
      return this.event?.requirementsJson?.find((requirement) => requirement.cardType?.aggregateType === true)
    },
    entityCardType () {
      return this.getAggregateCardType(this.requirementTypes)
    },
    entityCardColor () {
      return requirementTypesApi.getBackgroundColour(this.entityCardType)
    }
  },

  watch: {
    dataModel: {
      handler () {
        this.refreshKey++
      },
      deep: true
    }
  },

  methods: {
    blurDataModelName (event) {
      const newName = (event.target.innerText || '').trim()

      if (newName.length === 0) {
        this.restoreValue(event, this.dataModel.name)
        return
      }

      if (newName.length > 30) {
        alert('Entity name must be max 30 characters')
        this.restoreValue(event, this.dataModel.name)
        return
      }

      this.$emit('blur-data-model-name', event)
    },
    isOneToMany (dataField) {
      if (!this.dataModel.id || !dataField.relatedEntityId) {
        return false
      }

      if (!this.dataModel) {
        return false
      }

      const matchingField = this.dataModel.dataFields?.find((field) => field.relatedEntityId === dataField.relatedEntityId)

      return matchingField?.cardinality === 'one-to-many'
    },
    focusOnField (id) {
      document.getElementById(id).focus()
    },
    focusOnRef (ref) {
      if (this.$refs[ref]) {
        if (this.$refs[ref][0].setFocus) {
          this.$refs[ref][0].setFocus()
        } else if (this.$refs[ref].setFocus) {
          this.$refs[ref].setFocus()
        } else {
          document.getElementById(ref).focus()
        }
      }
    },
    getNumberOfExampleDataRows (dataFields) {
      let numberOfExampleDataRows = 0
      const allExampleDataArrays = dataFields.map(field => field.exampleData)
      for (const exampleDataArray of allExampleDataArrays) {
        for (const [index, exampleData] of exampleDataArray.entries()) {
          if (exampleData && exampleData.trim().length > 0) {
            if (index > numberOfExampleDataRows) {
              numberOfExampleDataRows = index
            }
          }
        }
      }
      return numberOfExampleDataRows
    },
    idTable (indexDataModel, row, column) {
      return (indexDataModel + 1) * 10000 + row * 100 + column
    },
    leaveCell (event, indexDataModel, row, indexDataField) {
      const ref = this.idTable(indexDataModel, row, indexDataField)
      switch (event.keyCode) {
        case 13: // enter
          this.focusOnRef(ref + 100) // move one cell down
          break
        case 37: // left arrow
          this.focusOnRef(ref - 1) // move left
          break
        case 38: // up arrow
          this.focusOnRef(ref - 100) // move down
          break
        case 39: // right arrow
          this.focusOnRef(ref + 1) // move right
          break
        case 40: // down arrow
          this.focusOnRef(ref + 100) // move down
          break
      }
      event.preventDefault()
      event.stopPropagation()
    },
    generateExampleData (entityIndex, dataFieldIndex) {
      this.$emit('generate-example-data', entityIndex, dataFieldIndex)
    },
    async addEntityCard (eventId, cardTypeId, text) {
      await workflowApi.createRequirementJson(this.workflow.id, eventId, {
        cardTypeId,
        description: text
      })
    },
    initSortable () {
      if (!this.$refs.sortableList) return

      this.sortable = new Sortable(this.$refs.sortableList[0], {
        draggable: '.sortableRow',
        handle: '.sortHandle',
        ghostClass: 'sortable-ghost', // Class name for the drop placeholder
        chosenClass: 'sortable-chosen', // Class name for the chosen item
        dragClass: 'sortable-drag',
        animation: 150,
        scroll: true,
        forceFallback: false,
        scrollSensitivity: 100,
        onEnd: this.onEndDrag,
        onStart: function (event) {
          Array.prototype.forEach.call(
            document.querySelectorAll('.v-row-group__header'),
            function (el, index) {
              if (!el.classList.contains('sortableRow') && index !== 0) {
                el.classList.add('sortableRow')
              }
            }
          )
          Array.prototype.forEach.call(
            document.querySelectorAll('.sortableRow:not(.sortable-chosen)'),
            function (el, index) {
              if (!el.classList.contains('white-background-on-hover') && index !== 0) {
                el.classList.add('white-background-on-hover')
              }
            }
          )
        }
      })
    },
    async onEndDrag (dropEvent) {
      const fromIndex = dropEvent.oldIndex - 1
      const toIndex = dropEvent.newIndex - 1

      if (this.dataModel.type === 'Dimension') {
        const modelIndex = this.workflow.dataModels.findIndex(model => model.id === this.dataModel.id)
        const newDataModels = structuredClone(this.workflow.dataModels || [])

        const field = newDataModels[modelIndex].dataFields[fromIndex]
        newDataModels[modelIndex].dataFields.splice(fromIndex, 1)
        newDataModels[modelIndex].dataFields.splice(toIndex, 0, field)

        await workflowApi.updateDataModels({
          dataModelsOnWorkflow: newDataModels,
          workflow: this.workflow
        })
      } else {
        const modelIndex = this.event.dataModels.findIndex(model => model.type === this.dataModel.type)

        const moveFields = (event) => {
          const field = event.dataModels[modelIndex].dataFields[fromIndex]
          event.dataModels[modelIndex].dataFields.splice(fromIndex, 1)
          event.dataModels[modelIndex].dataFields.splice(toIndex, 0, field)
        }

        EventBus.$emit('update-data-model-with-function', { updateFunction: moveFields, eventId: this.event.id })
      }
    },
    isMissingEntity (commandField) {
      if (this.dataModel.type === 'Dimension') return false
      if (!this.dataModel.cardId) {
        return false
      }

      const rootEntityId = this.event.requirementsJson?.find((card) => card.id === this.dataModel.cardId)?.entityId

      const workflowModels = this.workflow?.dataModels || []
      const eventModels = this.event?.dataModels || []

      if (!workflowModels.length || !eventModels.length) return true

      const availableFields = new Set(
        workflowModels
          .filter(dataModel =>
            eventModels.some(eventModel =>
              eventModel.type === this.dataModel.type &&
              (eventModel.dataFields?.some(field => field.relatedEntityId === dataModel.id) ||
                rootEntityId === dataModel.id)
            )
          )
          .flatMap(dataModel => dataModel.dataFields?.map(field => field.name) || [])
      )

      return !availableFields.has(commandField)
    }
  },

  mounted () {
    this.initSortable()
  }
}
</script>

<script setup>
const { setPrimaryKey, setReference, setFieldMandatory } = useEntityActions()
</script>

<style>
.beam-table-container .v-table__wrapper {
  padding-top: 12px;
  padding-bottom: 2px;
}

.main-badge{
  font-size: 14px;
  padding: 5px 8px;
  border-radius: 4px;
  background: #F5F2F0;
  color: var(--text-dark, #1F2833);
  max-width: fit-content;
}

.header-cell {
  font-size: 14px;
  font-weight: 500;
  padding: 6px 16px 6px 16px;
  background-color: #FCFCFC;

  &.header-cell-verb {
    border-bottom: thin solid var(--blue-2) !important;
  }
}

.header-action {
  top: 50%;
  right: 10px;
  visibility: hidden;
  cursor: pointer;
  line-height: 33px;
  position: absolute;
  background-color: var(--blue-2);
  transform: translateY(-50%);
  color: var(--text-dark, #1F2833);
  display: flex;
  align-items: center;

  .v-icon {
    display: none;
  }
}

.beam-table-cell-select {
  background-color: #DEEBFA;
  border-bottom: 1px solid #EDEAE8 !important;
}

.relationship-type {
  .beam-table-cell-select {
    background-color: #E8F5E9;
  }

  .v-btn {
    background-color: #E8F5E9 !important;
  }
}

.field-name-wrapper {
  height: 100%;
  position: relative;
}

.field-name-wrapper:hover .header-action {
  visibility: visible;
}

.primary-key-icon {
  background-color: var(--blue-2);
}

.field-name-wrapper:hover .header-action .v-icon {
  display: block;
}

.header-action:focus .v-icon {
  visibility: visible;
}

.add-field-button {
  width: 50px;
  background-color: var(--blue-2);
  border-bottom: thin solid #EDEAE8 !important;
}

.add-field-button:hover {
  cursor: pointer;
  background-color: #A3C2E6;
}

tr > td:focus-within, td:focus {
  z-index: 3000;
  position: relative;
  outline: 2px solid #4D90FE;
}

.v-btn__content div {
  outline: none;
}
.beam-table-container table tbody td,
.beam-table-container table.v-table tbody th {
  height: 36px !important;
  min-width: 150px !important;
}

.table-container {
  border-bottom: 1px solid transparent !important
}

.field-label {
  width: 132px;
  font-size: 12px;
  background: #F5F2F0;
  color: #4F5359;
  padding: 6px 24px 6px 24px !important;
}

tr:not(:last-child) .field-label {
  border-bottom: 1px solid #E5E3E1 !important;
}

.order-last {
  order: 200;
}

.sortable-chosen {
  background: #eee;
}

.sortable-ghost {
  background: #E5E3E1;
}

.sortable-ghost > div {
  opacity: 0.01;
}

.add-card-type-btn {
  min-width: 200px !important;
  border: 2px solid var(--command-border-color) !important;
}
</style>

<style scoped>
.sortableRow:hover .sortHandle {
  display: inline-flex !important;
}

.sortHandle {
  position: absolute !important;
  width: 36px !important;
  height: 24px !important;
  font-size: 28px !important;
  display: none !important;
  top: -12px;
  left: calc(50% - 18px);
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
}

.sortHandle:hover {
  cursor: grab;
}
</style>
